import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { PaiMetric } from '@upright/common/model/input/pai-assessments/types';

interface ImpactCategoryByPai {
  [id: string]: ImpactCategoryId[];
}

interface PaisByImpactCategory {
  [id: string]: PaiMetric[];
}

const impactCategoryByPaiMapping: ImpactCategoryByPai = {
  [PaiMetric.GHG1]: [ImpactCategoryId.GHG_EMISSIONS],
  [PaiMetric.GHG2]: [ImpactCategoryId.GHG_EMISSIONS],
  [PaiMetric.GHG3]: [ImpactCategoryId.GHG_EMISSIONS],
  [PaiMetric.GHGF12]: [ImpactCategoryId.GHG_EMISSIONS],
  [PaiMetric.GHGI]: [ImpactCategoryId.GHG_EMISSIONS],
  [PaiMetric.GHGI12]: [ImpactCategoryId.GHG_EMISSIONS],
  [PaiMetric.AIFF]: [ImpactCategoryId.GHG_EMISSIONS],
  [PaiMetric.NRNW]: [ImpactCategoryId.GHG_EMISSIONS],
  [PaiMetric.HICS]: [ImpactCategoryId.GHG_EMISSIONS],
  [PaiMetric.TECO]: [ImpactCategoryId.GHG_EMISSIONS],
  [PaiMetric.NRGI]: [ImpactCategoryId.GHG_EMISSIONS],
  [PaiMetric.BIOD]: [ImpactCategoryId.BIODIVERSITY],
  [PaiMetric.WTRE]: [ImpactCategoryId.SCARCE_NATURAL_RESOURCES],
  [PaiMetric.HAZW]: [ImpactCategoryId.WASTE],
  [PaiMetric.SOCV]: [ImpactCategoryId.JOBS], // not a great mapping
  [PaiMetric.SOCP]: [ImpactCategoryId.JOBS], // not a great mapping
  [PaiMetric.PAYG]: [ImpactCategoryId.EQUALITY],
  [PaiMetric.FMBR]: [ImpactCategoryId.EQUALITY],
  [PaiMetric.CVWP]: [ImpactCategoryId.SOCIETAL_STABILITY],
  [PaiMetric.CHEM]: [ImpactCategoryId.NON_GHG_EMISSIONS, ImpactCategoryId.WASTE],
};

function preparePaisByImpactCategory() {
  const pais = Object.keys(impactCategoryByPaiMapping) as PaiMetric[];
  const ret: PaisByImpactCategory = {};
  pais.forEach(pai => {
    const impacts = impactCategoryByPaiMapping[pai];
    impacts.forEach(impact => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!ret[impact]) {
        ret[impact] = [];
      }
      ret[impact].push(pai);
    });
  });
  return ret;
}

const impactPaisMapping: PaisByImpactCategory = preparePaisByImpactCategory();

export function getPaisForImpactCategory(id: ImpactCategoryId): PaiMetric[] {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return impactPaisMapping[id] || [];
}

// [PaiMetric.GHG1]: 'Scope 1 GHG emissions',
//     [PaiMetric.GHG2]: 'Scope 2 GHG emissions',
//     [PaiMetric.GHG3]: 'Scope 3 GHG emissions',
//     [PaiMetric.GHGF]: 'Carbon footprint (Scope 1, 2 and 3)',
//     [PaiMetric.GHGF12]: 'Carbon footprint (Scope 1 and 2)',
//     [PaiMetric.GHGI]: 'GHG intensity (Scope 1, 2 and 3)',
//     [PaiMetric.GHGI12]: 'GHG intensity (Scope 1 and 2)',
//     [PaiMetric.AIFF]: 'Fossil fuel sector activity',
//     [PaiMetric.NRNW]: 'Non-renewable energy share',
//     [PaiMetric.HICS]: 'High impact climate sector',
//     [PaiMetric.TECO]: 'Total energy consumption',
//     [PaiMetric.NRGI]: 'Energy consumption intensity',
//     [PaiMetric.BIOD]: 'Activities negatively affecting biodiversity-sensitive areas',
//     [PaiMetric.WTRE]: 'Emissions to water',
//     [PaiMetric.HAZW]: 'Hazardous waste',
//     [PaiMetric.SOCV]: 'UNGC/OECD norm violations',
//     [PaiMetric.SOCP]: 'UNGC/OECD compliance mechanisms',
//     [PaiMetric.PAYG]: 'Unadjusted gender pay gap',
//     [PaiMetric.FMBR]: 'Board gender diversity',
//     [PaiMetric.CVWP]: 'Involvement in controversial weapons',
//     [PaiMetric.CHEM]: 'Production of chemicals',
